import { Injectable, NgZone } from "@angular/core";
import {
  HttpEvent,
  HttpEventType,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { Observable } from "rxjs";

/**
 * Re-enters the Angular zone before handing an HTTP response to the subscriber.
 *
 * Token acquisition in MsalInterceptor resolves its promise chain outside the
 * Angular zone, so every response continuation downstream of it also runs
 * outside the zone and schedules no change detection pass. Components then set
 * their state correctly but the view is never repainted until an unrelated
 * event (such as refocusing the browser tab) happens to trigger a tick.
 *
 * This must be the first interceptor registered so that it sits closest to the
 * subscriber on the response path and therefore wraps every callback below it.
 * Where a callback is already inside the zone, zone.run is a no-op.
 */
@Injectable()
export class NgZoneRestoreInterceptor implements HttpInterceptor {
  constructor(private zone: NgZone) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return new Observable<HttpEvent<any>>((observer) =>
      // Returning the inner subscription preserves teardown, so cancelling the
      // outer subscription still aborts the in-flight request.
      next.handle(request).subscribe({
        // Only the final response needs a change detection pass. Re-entering
        // the zone for Sent and progress events as well would cost a full
        // ApplicationRef.tick() each, which is expensive on the grid-heavy
        // pages that issue dozens of requests per load.
        next: (event) =>
          event.type === HttpEventType.Response
            ? this.zone.run(() => observer.next(event))
            : observer.next(event),
        error: (error) => this.zone.run(() => observer.error(error)),
        // No application code reacts to completion, and the response above has
        // already ticked, so completing outside the zone saves another pass.
        complete: () => observer.complete(),
      })
    );
  }
}
